import React, { useEffect, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import ProductGroup from '../../../../../components/common/ProductGroup';
import CarItemCodeDropdown from '../../../../../components/common/CarItemCodeDropdown';
import LaborGroupDropdown from '../../../../../components/common/LaborGroupDropdown';
import ModelDropdown from '../../../../../components/common/ModelDropdown';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';

// Helper function for flag class names (assuming you have a flag icon library)
const getFlagClassNameForDialog = (languageCode) => {
    if (!languageCode) return 'fi fi-xx';
    const codeToFlagSuffixMap = {
        en: 'us',
        tr: 'tr',
        de: 'de',
        fr: 'fr',
        it: 'it',
        es: 'es',
        ro: 'ro'
    };
    const lowerCaseCode = languageCode.toLowerCase();
    const flagSuffix = codeToFlagSuffixMap[lowerCaseCode];
    return flagSuffix ? `fi fi-${flagSuffix}` : `fi fi-${lowerCaseCode}`;
};

const SUPPORTED_LANGUAGES = [
    { id: 1, code: 'en', name: 'English' },
    { id: 2, code: 'tr', name: 'Türkçe' },
    { id: 3, code: 'fr', name: 'Français' },
    { id: 4, code: 'de', name: 'Deutsch' },
    { id: 5, code: 'it', name: 'Italiano' },
    { id: 6, code: 'ro', name: 'Română' }
].sort((a, b) => {
    if (a.code === 'en') return -1;
    if (b.code === 'en') return 1;
    if (a.code === 'tr') return -1;
    if (b.code === 'tr') return 1;
    return a.name.localeCompare(b.name);
});

const LaborCatalogDialog = ({ visible, onHide, onSubmit, initialData, loading, isEdit, th }) => {
    const supportedLanguages = SUPPORTED_LANGUAGES;

    const getInitialTranslations = () => {
        return supportedLanguages.map((lang) => ({
            text: '',
            languageId: lang.id
        }));
    };

    const initialFormState = {
        startDate: null,
        endDate: null,
        productGroup: '',
        model: '', // Model başlangıçta boş string veya null olabilir
        carItemCode: '',
        laborGroup: null,
        status: 0,
        laborCode: '',
        laborTime: null,
        isActive: 1,
        translations: getInitialTranslations()
    };

    const [formData, setFormData] = useState(initialFormState);

    const toast = useRef(null);
    const [errors, setErrors] = useState({});
    const [localSubmitted, setLocalSubmitted] = useState(false);

    useEffect(() => {
        if (visible) {
            setLocalSubmitted(false);
            setErrors({});

            if (isEdit && initialData) {
                const fixedStartDate = typeof initialData.startDate === 'string' ? new Date(initialData.startDate) : initialData.startDate;
                const fixedEndDate = typeof initialData.endDate === 'string' ? new Date(initialData.endDate) : initialData.endDate;

                const backendTranslations = initialData.translations || [];
                const updatedTranslations = supportedLanguages.map((lang) => {
                    const existingTranslation = backendTranslations.find((t) => t.languageId === lang.id);
                    return existingTranslation ? { ...existingTranslation } : { text: '', languageId: lang.id };
                });

                // Model için özel durum: eğer initialData.model bir string ise, ModelDropdown'ın beklediği formata dönüştür
                const modelValue = initialData.model ? { id: null, name: initialData.model, displayValue: initialData.model } : null; // null olarak ayarlamak, ModelDropdown'ın placeholder göstermesini sağlar

                setFormData({
                    ...initialData,
                    startDate: fixedStartDate,
                    endDate: fixedEndDate,
                    model: modelValue, // Düzeltilmiş model değeri
                    translations: updatedTranslations,
                    laborGroup: initialData.laborGroup === 0 ? null : initialData.laborGroup,
                    laborTime: initialData.laborTime === 0 ? null : initialData.laborTime
                });
            } else {
                setFormData(initialFormState); // Yeni kayıt modunda formu sıfırla
            }
        }
    }, [visible, isEdit, initialData, supportedLanguages]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setLocalSubmitted(true);

        const validationErrors = [];
        const newErrors = {};

        if (!formData.startDate) {
            validationErrors.push(th('laborCatalog.form.startDateRequired'));
            newErrors.startDate = th('laborCatalog.form.startDateRequired');
        }

        if (!formData.productGroup) {
            validationErrors.push(th('laborCatalog.form.productGroupRequired'));
            newErrors.productGroup = th('laborCatalog.form.productGroupRequired');
        }

        if (!formData.laborCode.trim()) {
            validationErrors.push(th('laborCatalog.form.laborCodeRequired'));
            newErrors.laborCode = th('laborCatalog.form.laborCodeRequired');
        }

        if (formData.laborTime == null || isNaN(formData.laborTime) || formData.laborTime <= 0) {
            validationErrors.push(th('laborCatalog.form.laborTimeRequired'));
            newErrors.laborTime = th('laborCatalog.form.laborTimeRequired');
        }

        supportedLanguages.forEach((lang) => {
            const translation = formData.translations.find((t) => t.languageId === lang.id);
            const isRequired = lang.code === 'tr' || lang.code === 'en';
            if (isRequired && (!translation || !translation.text.trim())) {
                validationErrors.push(th('laborCatalog.form.translationRequired', { language: lang.name }));
                newErrors[`translation_${lang.code}`] = th('laborCatalog.form.translationRequired', { language: lang.name });
            }
        });

        setErrors(newErrors);

        if (validationErrors.length > 0) {
            validationErrors.forEach((error, index) => {
                setTimeout(() => {
                    toast.current?.show({
                        severity: 'error',
                        summary: th('laborCatalog.messages.missingField'),
                        detail: error,
                        life: 4000
                    });
                }, index * 200);
            });
            return;
        }

        const payload = {
            ...formData,
            model: formData.model && typeof formData.model === 'object' ? formData.model.name : null,
            startDate: formData.startDate ? formData.startDate.toISOString() : null,
            endDate: formData.endDate ? formData.endDate.toISOString() : null,
            laborGroup: formData.laborGroup === null ? 0 : formData.laborGroup,
            laborTime: formData.laborTime == null ? 0 : formData.laborTime
        };

        onSubmit(payload);
    };

    const statusOptions = [
        { label: th('laborCatalog.status.active'), value: 0 },
        { label: th('laborCatalog.status.inactive'), value: 1 },
        { label: th('laborCatalog.status.cancelled'), value: 2 }
    ];

    const handleInputChange = (field, value) => {
        setFormData((prev) => {
            let updated = {
                ...prev,
                [field]: value
            };

            if (field === 'productGroup') {
                // Ürün grubu değişirse model ve carItemCode da sıfırlanmalı (genelde ikisi bağlı olur)
                updated.model = null;
                updated.carItemCode = '';
            }

            return updated;
        });
        // Hata varsa ve yeni değer geçerliyse hatayı temizle
        if (errors[field] && value !== null && value !== undefined && value !== '' && (typeof value !== 'object' || (typeof value === 'object' && value.name))) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleTranslationChange = (languageId, newText) => {
        setFormData((prev) => {
            const updatedTranslations = prev.translations.map((t) => (t.languageId === languageId ? { ...t, text: newText } : t));

            if (!updatedTranslations.some((t) => t.languageId === languageId)) {
                updatedTranslations.push({ languageId, text: newText });
            }

            const langCode = supportedLanguages.find((lang) => lang.id === languageId)?.code;
            if (langCode && errors[`translation_${langCode}`] && newText.trim()) {
                setErrors((prevErrors) => {
                    const newErrors = { ...prevErrors };
                    delete newErrors[`translation_${langCode}`];
                    return newErrors;
                });
            }

            return {
                ...prev,
                translations: updatedTranslations
            };
        });
    };

    const renderFooter = () => (
        <div>
            <Button label={th('laborCatalog.dialog.close')} icon="pi pi-times" onClick={onHide} outlined/>
            <Button label={th('laborCatalog.dialog.save')} icon="pi pi-check" onClick={handleSubmit} loading={loading} />
        </div>
    );

    return (
        <Dialog visible={visible} style={{ width: '900px' }} header={isEdit ? th('laborCatalog.editTitle') : th('laborCatalog.addTitle')} modal className="p-fluid" footer={renderFooter()} onHide={onHide}>
            <Toast ref={toast} />
            <div className="grid">
                <div className="col-6">
                    <div className="field">
                        <label htmlFor="startDate">{th('laborCatalog.form.startDate')} *</label>
                        <Calendar id="startDate" value={formData.startDate} onChange={(e) => handleInputChange('startDate', e.value)} dateFormat="dd/mm/yy" showIcon className={localSubmitted && !formData.startDate ? 'p-invalid' : ''} />
                        {localSubmitted && !formData.startDate && <small className="p-error">{th('laborCatalog.form.startDateRequired')}</small>}
                    </div>
                </div>

                <div className="col-6">
                    <div className="field">
                        <label htmlFor="endDate">{th('laborCatalog.form.endDate')}</label>
                        <Calendar id="endDate" value={formData.endDate} onChange={(e) => handleInputChange('endDate', e.value)} dateFormat="dd/mm/yy" showIcon minDate={formData.startDate} />
                    </div>
                </div>

                <div className="col-6">
                    <div className="field">
                        <label htmlFor="productGroup">{th('laborCatalog.form.productGroup')} *</label>
                        <ProductGroup
                            value={formData.productGroup}
                            onChange={(value) => handleInputChange('productGroup', value)}
                            placeholder={th('laborCatalog.form.productGroupPlaceholder')}
                            className={localSubmitted && !formData.productGroup ? 'p-invalid' : ''}
                        />
                        {localSubmitted && !formData.productGroup && <small className="p-error">{th('laborCatalog.form.productGroupRequired')}</small>}
                    </div>
                </div>

                <div className="col-6">
                    <div className="field">
                        <label htmlFor="model">{th('laborCatalog.form.model')}</label>
                        {console.log('modelllll:', formData.model)}

                        <ModelDropdown
                            disabled={!formData.productGroup}
                            value={formData.model} // Model artık bir obje veya null
                            onChange={(value) => handleInputChange('model', value)}
                            productGroup={formData.productGroup}
                            placeholder={th('laborCatalog.form.modelPlaceholder')}
                        />
                    </div>
                </div>

                <div className="col-6">
                    <div className="field">
                        <label htmlFor="carItemCode">{th('laborCatalog.form.carItemCode')}</label>
                        <CarItemCodeDropdown
                            value={formData.carItemCode}
                            onChange={(value) => handleInputChange('carItemCode', value)}
                            productGroup={formData.productGroup}
                            disabled={!formData.productGroup}
                            placeholder={th('laborCatalog.form.carItemCodePlaceholder')}
                        />
                    </div>
                </div>

                <div className="col-6">
                    <div className="field">
                        <label htmlFor="laborGroup">{th('laborCatalog.form.laborGroup')}</label>
                        <LaborGroupDropdown value={formData.laborGroup} onChange={(value) => handleInputChange('laborGroup', value)} placeholder={th('laborCatalog.form.laborGroupPlaceholder')} />
                    </div>
                </div>

                <div className="col-6">
                    <div className="field">
                        <label htmlFor="laborCode">{th('laborCatalog.form.laborCode')} *</label>
                        <InputText id="laborCode" value={formData.laborCode} onChange={(e) => handleInputChange('laborCode', e.target.value)} className={localSubmitted && !formData.laborCode.trim() ? 'p-invalid' : ''} />
                        {localSubmitted && !formData.laborCode.trim() && <small className="p-error">{th('laborCatalog.form.laborCodeRequired')}</small>}
                    </div>
                </div>
                <div className="col-6">
                    <div className="field">
                        <label htmlFor="laborTime">{th('laborCatalog.form.laborTime')} *</label>
                        <InputNumber
                            id="laborTime"
                            value={formData.laborTime}
                            onValueChange={(e) => handleInputChange('laborTime', e.value)}
                            mode="decimal"
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            className={localSubmitted && (formData.laborTime == null || formData.laborTime <= 0) ? 'p-invalid' : ''}
                        />
                        {localSubmitted && (formData.laborTime == null || formData.laborTime <= 0) && <small className="p-error">{th('laborCatalog.form.laborTimeRequired')}</small>}
                    </div>
                </div>

                {/* Dynamically rendered Translation fields */}
                {supportedLanguages.map((lang) => {
                    const translation = formData.translations.find((t) => t.languageId === lang.id);
                    const currentText = translation?.text || '';
                    const isRequired = lang.code === 'tr' || lang.code === 'en';
                    const inputId = `translation_text_${lang.code}`;

                    return (
                        <div className="col-6" key={lang.id}>
                            <div className="field">
                                <label htmlFor={inputId}>
                                    <span className={getFlagClassNameForDialog(lang.code)} style={{ fontSize: '1.2em', marginRight: '0.5em' }} title={lang.name}></span>
                                    {lang.name} {isRequired && <span className="text-red-500">*</span>}
                                </label>
                                <InputText id={inputId} value={currentText} onChange={(e) => handleTranslationChange(lang.id, e.target.value)} className={localSubmitted && isRequired && !currentText.trim() ? 'p-invalid' : ''} />
                                {localSubmitted && isRequired && !currentText.trim() && <small className="p-error">{th('laborCatalog.form.translationRequired', { language: lang.name })}</small>}
                            </div>
                        </div>
                    );
                })}

                <div className="col-6">
                    <div className="field">
                        <label htmlFor="status">{th('laborCatalog.form.status')}</label>
                        <Dropdown id="status" value={formData.status} options={statusOptions} onChange={(e) => handleInputChange('status', e.value)} placeholder={th('laborCatalog.form.statusPlaceholder')} />
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default LaborCatalogDialog;
